import { useEffect, useState } from "react";
import { FaPlay, FaArrowRight } from "react-icons/fa";

const getYoutubeVideoId = (url) => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);

  return match ? match[1] : null;
};

const getYoutubeThumbnail = (url) => {
  const videoId = getYoutubeVideoId(url);

  if (!videoId) {
    return "/images/video-placeholder.webp";
  }

  return `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`;
};

const VideoSkeleton = () => {
  return (
    <div className="animate-pulse bg-white rounded-3xl p-4 border border-gray-100">
      <div className="bg-[#8aa0ba] w-full aspect-video rounded-2xl"></div>

      <div className="mt-4 space-y-3">
        <div className="h-4 bg-[#8aa0ba] rounded w-3/4"></div>
        <div className="h-3 bg-[#8aa0ba] rounded w-full"></div>
        <div className="h-3 bg-[#8aa0ba] rounded w-2/3"></div>
        <div className="h-4 bg-[#8aa0ba] rounded w-32 mt-4"></div>
      </div>
    </div>
  );
};

export default function TutorialVideosMain({ videos = [], isVideoloading }) {
  const filteredVideos = videos?.slice(0, 3);

  return (
    <section className="bg-[#FFFFFF] py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-[#10324F] text-xl md:text-3xl font-bold">
            Essential Waggex Tutorials for New Users
          </h2>

          <p className="mt-4 text-[#10324F] max-w-3xl mx-auto text-base md:text-lg">
            Follow step-by-step video guides designed to help you set up Waggex
            correctly and manage payroll, attendance, and employee operations
            with confidence
          </p>
        </div>

        {/* Videos / Skeletons */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
          {isVideoloading
            ? Array.from({ length: 3 }).map((_, index) => (
                <VideoSkeleton key={index} />
              ))
            : filteredVideos?.map((video) => (
                <div
                  key={video._id}
                  className="bg-white rounded-3xl p-4 border border-gray-100"
                  style={{
                    backdropFilter: "blur(30px)",
                    WebkitBackdropFilter: "blur(30px)",
                    boxShadow: `
                      inset -15px -15px 20px 0px rgba(209, 223, 234, 0.4),
                      -20px -20px 30px 0px rgba(209, 223, 234, 0.3)
                    `,
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl">
                    <img
                      src={getYoutubeThumbnail(video.videoLink)}
                      alt={video.title}
                      className="w-full aspect-video object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = "/images/video-placeholder.webp";
                      }}
                    />

                    <a
                      href={video.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Watch ${video.title} on YouTube`}
                      className="absolute bottom-4 right-4 w-14 h-14 rounded-full bg-white/40 border border-white backdrop-blur-md flex items-center justify-center hover:scale-105 transition"
                    >
                      <FaPlay className="text-white ml-1" />
                    </a>
                  </div>

                  <div className="pt-5">
                    <h2 className="font-bold text-[#10324F] text-sm line-clamp-2 mb-2 min-h-8">
                      {video.title}
                    </h2>

                    <p className="text-[#10324F] text-xs leading-tight min-h-12 mb-2 hidden sm:block">
                      {video.description}
                    </p>

                    <a
                      href={video.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#10324F] underline text-sm hover:gap-3 transition-all"
                    >
                      Watch on Youtube
                      <FaArrowRight size={12} />
                    </a>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </section>
  );
}
